import React, { useState } from "react"
import { TimePicker } from "@mui/x-date-pickers/TimePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { TextField } from "@mui/material"
import dayjs from "dayjs"
import { useCombobox, UseComboboxStateChangeTypes } from "downshift"
import clsx from "clsx"
import { EventPerformanceType } from "../../types/events"

const MIN_LENGTH_BAND_NAME = 2

// Consistent styling for all MUI inputs
const muiInputStyles = {
  width: "100%",
}

const initialValues = {
  pending_band_email: "",
  band_id: null,
  band_name: "",
  set_time: null,
  search_query: "",
  search_results: [],
  show_email_field: false,
  is_added: false,
  _destroy: false,
}

interface EventPerformanceProps {
  index: number
  onAddToLineup: (performance: EventPerformanceType) => void
  onUpdatePerformance?: (performance: EventPerformanceType) => void
  onCancel?: () => void
  eventStartDate: Date | null
  existingPerformance?: EventPerformanceType | null
  isEditing?: boolean
}

const EventPerformanceForm: React.FC<EventPerformanceProps> = ({
  index,
  onAddToLineup,
  onUpdatePerformance,
  onCancel,
  eventStartDate,
  existingPerformance = null,
  isEditing = false,
}) => {
  // Email validation function - matches backend validation
  const isValidEmail = (email: string): boolean => {
    if (email.trim() === "") return true // Blank email is valid
    const emailRegex =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
    return emailRegex.test(email)
  }
  const [performance, setPerformance] = useState<EventPerformanceType>({
    ...initialValues,
    ...existingPerformance,
  })
  const [isEmailFocused, setIsEmailFocused] = useState(false)
  const searchBands = async (query: string) => {
    if (query.length < 2) {
      updateEventPerformance({ search_results: [] })
      return
    }

    try {
      const response = await fetch(
        `/api/v1/search/bands/${encodeURIComponent(query)}`,
      )
      const data = await response.json()

      if (data.success && data.bands?.length > 0) {
        updateEventPerformance({ search_results: data.bands })
      } else {
        updateEventPerformance({ search_results: [] })
      }
    } catch (error) {
      console.error("Error searching bands:", error)
      updateEventPerformance({ search_results: [] })
    }
  }

  const updateEventPerformance = (updates) => {
    setPerformance((prevPerformance) => {
      const updatedPerformance = { ...prevPerformance, ...updates }
      return updatedPerformance
    })
  }

  // Single method to handle all focus logic after band name loses focus
  const focusNextFieldAfterBand = () => {
    setTimeout(() => {
      if (!performance.band_id) {
        const emailInput = document.getElementById(
          "performance-email",
        ) as HTMLInputElement
        if (emailInput) {
          emailInput.focus()
        }
      }
    }, 10)
  }

  const selectBand = (band: any) => {
    updateEventPerformance({
      band_id: band.id,
      band_name: band.name,
      search_query: band.name,
      show_email_field: false,
      search_results: [],
    })

    // Update the downshift input value to match the selected band
    setInputValue(band.name)

    // Focus the appropriate next field based on band type
    focusNextFieldAfterBand()
  }

  const handleAddToLineup = () => {
    if (isEditing && onUpdatePerformance) {
      onUpdatePerformance({ ...performance })
    } else {
      onAddToLineup({ ...performance })
    }
    resetPerformance()
  }

  const resetPerformance = () => {
    setInputValue("")
    updateEventPerformance({
      pending_band_email: "",
      band_name: "",
      band_id: null,
      search_query: "",
      search_results: [],
      set_time: null,
      show_email_field: false,
    })
  }

  const handleBandSearchChange = (query: string) => {
    updateEventPerformance({ search_query: query, band_name: query })

    if (query.length === 0) {
      resetPerformance()
    } else if (query.length >= 2) {
      searchBands(query)
    } else {
      // For short queries (1 character), just clear search results but keep typing
      updateEventPerformance({ search_results: [] })
    }
  }

  const selectTypes: UseComboboxStateChangeTypes[] = [
    useCombobox.stateChangeTypes.ItemClick,
    useCombobox.stateChangeTypes.InputKeyDownEnter,
  ]

  // Downshift combobox hook
  const {
    isOpen,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
    setInputValue,
  } = useCombobox({
    items: performance.search_results,
    initialInputValue: performance.band_name,
    onInputValueChange: ({ inputValue, type }) => {
      if (selectTypes.includes(type)) {
        // Item was selected let onSelectedItemChange handle it
        return
      }
      if (inputValue !== undefined) {
        handleBandSearchChange(inputValue)
      }
    },
    onSelectedItemChange: ({ selectedItem }) => {
      if (selectedItem) {
        selectBand(selectedItem)
        closeMenu()
      }
    },
    itemToString: (item) => (item ? item.name : ""),
  })

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="mb-6" data-performance-id={`new-performance-${index}`}>
        <label className="block col-span-12 text-gray-900 text-xs uppercase font-bold">
          {isEditing ? "Update Performance" : "Add Band to Lineup"}
        </label>
        {/* Band Field with Downshift Autocomplete */}
        <div className="mb-3 grid grid-cols-12 gap-3">
          <div className="relative col-span-11">
            <TextField
              {...getInputProps({
                name: "band_name",
                placeholder: "Who is playing?",
                onFocus: () => {
                  if (performance.search_results.length > 0) {
                    openMenu()
                  }
                },
                onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
                  focusNextFieldAfterBand()
                },
              })}
              size="small"
              sx={{
                ...muiInputStyles,
                "& .MuiInputBase-input": {
                  ...muiInputStyles["& .MuiInputBase-input"],
                  color: performance.band_id ? "#2f855a" : "#374151", // green-800 if band selected
                  fontWeight: performance.band_id ? "bold" : "normal",
                },
              }}
            />

            {/* Autocomplete Dropdown */}
            <ul
              {...getMenuProps()}
              className={clsx(
                "absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto",
                {
                  hidden: !isOpen || performance.search_results?.length === 0,
                },
              )}
            >
              {isOpen &&
                performance.search_results?.map((band, index) => (
                  <li
                    key={band.id}
                    {...getItemProps({ item: band, index })}
                    className={clsx(
                      "px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 flex items-center space-x-3",
                      highlightedIndex === index && "bg-blue-100 font-medium",
                      "hover:bg-gray-100",
                    )}
                  >
                    <div className="flex-shrink-0 mr-2">
                      {band.cover_image_url ? (
                        <img
                          src={band.cover_image_url}
                          alt={`${band.name} cover`}
                          className="w-10 h-10 rounded-sm object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-sm bg-gray-200 flex items-center justify-center">
                          <span
                            className="text-gray-400 text-xs"
                            role="img"
                            aria-label="music note"
                          >
                            🎵
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <div className="font-medium leading-tight">
                        {band.name}
                      </div>
                      {band.location?.displayName && (
                        <div className="text-sm text-gray-600 leading-tight">
                          {band.location.displayName}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
          </div>

          {/* Show selected band info */}
          <div className="col-span-1">
            {performance.band_id && (
              <div className="">
                <span
                  role="img"
                  aria-label="band confirmed"
                  className="text-green-800 text-2xl"
                >
                  🤘
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Email Field (always rendered but conditionally hidden) */}
        <div
          className={clsx(
            "mb-3",
            (performance.band_name.length < MIN_LENGTH_BAND_NAME ||
              performance.band_id) &&
              "hidden",
          )}
        >
          <TextField
            id="performance-email"
            type="email"
            value={performance.pending_band_email}
            onChange={(e) => {
              const email = e.target.value.toLowerCase()
              updateEventPerformance({ pending_band_email: email })
            }}
            onFocus={() => setIsEmailFocused(true)}
            onBlur={(e) => {
              setIsEmailFocused(false)
            }}
            placeholder="Enter an email address for the band, and we'll invite them to join the show (optional)"
            size="small"
            error={
              !isEmailFocused &&
              performance.pending_band_email &&
              !isValidEmail(performance.pending_band_email)
            }
            helperText={
              !isEmailFocused &&
              performance.pending_band_email &&
              !isValidEmail(performance.pending_band_email)
                ? "Please enter a valid email address"
                : "This band will be invited to join bandNada"
            }
            sx={muiInputStyles}
          />
        </div>

        <div className="grid grid-cols-5">
          <div className="col-span-3 pr-4">
            <TimePicker
              value={(() => {
                if (!performance.set_time) return null
                if (performance.set_time instanceof Date) {
                  return dayjs(performance.set_time)
                }
                if (typeof performance.set_time === "string") {
                  try {
                    const date = new Date(performance.set_time)
                    return isNaN(date.getTime()) ? null : dayjs(date)
                  } catch (e) {
                    return null
                  }
                }
                return null
              })()}
              onChange={(time) =>
                updateEventPerformance({ set_time: time?.toDate() || null })
              }
              slotProps={{
                textField: {
                  placeholder: "What's their set time? (optional)",
                  disabled: !performance.band_name,
                  autoComplete: "off",
                  size: "small",
                  error: false,
                  helperText: "",
                  sx: muiInputStyles,
                },
              }}
              views={["hours", "minutes"]}
              minutesStep={15}
              format="h:mm a"
              minTime={(() => {
                if (
                  eventStartDate &&
                  eventStartDate instanceof Date &&
                  !isNaN(eventStartDate.getTime())
                ) {
                  // Use the hour from the event start date
                  return dayjs(eventStartDate)
                } else {
                  // Fallback to 8:00 PM if no event start date or invalid date
                  const date = new Date()
                  date.setHours(20, 0, 0, 0)
                  return dayjs(date)
                }
              })()}
            />
          </div>
          <div className="col-span-2">
            <button
              type="button"
              onClick={() => handleAddToLineup()}
              disabled={
                !performance.band_name ||
                (performance.pending_band_email &&
                  !isValidEmail(performance.pending_band_email))
              }
              className={clsx(
                "w-full focus:outline-none border px-4 py-2 text-sm rounded-sm transition-colors bg-white",
                !performance.band_name ||
                  (performance.pending_band_email &&
                    !isValidEmail(performance.pending_band_email))
                  ? "border-gray-300 text-gray-400 bg-gray-50 cursor-not-allowed"
                  : "border-gray-500 text-gray-700 hover:bg-gray-50 cursor-pointer",
              )}
            >
              {isEditing ? "Update Performance" : "Add to Lineup"}
            </button>
          </div>
        </div>
        {isEditing && onCancel && (
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-gray-600 hover:text-gray-800 underline focus:outline-none"
            >
              Cancel Edit
            </button>
          </div>
        )}
      </div>
    </LocalizationProvider>
  )
}

export default EventPerformanceForm
